/**
 * <ix-brands-folder> — iOS-style brand folders.
 * A closed folder shows a 2×2 preview of brand logos; clicking it animates
 * the card to a centered modal with a blurred overlay and a scrollable
 * 3-column grid of brand links.
 */
class IxBrandsFolder extends HTMLElement {
  #openCard = null;
  #placeholder = null;
  #lastRect = null;

  connectedCallback() {
    this.overlay = this.querySelector('.ix-brands-folder__overlay');
    this.querySelectorAll('.ix-brands-folder__card').forEach((card) => {
      card.addEventListener('click', (event) => {
        if (card.classList.contains('open')) return;
        event.preventDefault();
        this.#openCard ? this.#close(this.#openCard) : this.#open(card);
      });
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          card.classList.contains('open') ? this.#close(card) : this.#open(card);
        }
        if (event.key === 'Escape' && card.classList.contains('open')) this.#close(card);
      });
    });
    this.overlay?.addEventListener('click', () => {
      if (this.#openCard) this.#close(this.#openCard);
    });
  }

  #open(card) {
    if (this.#openCard) return;
    this.#lastRect = card.getBoundingClientRect();

    this.#placeholder = document.createElement('div');
    this.#placeholder.style.cssText = `width:${this.#lastRect.width}px;height:${this.#lastRect.height}px;visibility:hidden;margin:0 auto;`;
    card.parentNode.insertBefore(this.#placeholder, card);

    Object.assign(card.style, {
      position: 'fixed',
      top: `${this.#lastRect.top}px`,
      left: `${this.#lastRect.left}px`,
      width: `${this.#lastRect.width}px`,
      height: `${this.#lastRect.height}px`,
      margin: '0',
      zIndex: '1000',
    });

    if (this.overlay) {
      this.overlay.hidden = false;
      this.overlay.classList.add('active');
    }
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      card.classList.add('open');
      card.style.top = '50%';
      card.style.left = '50%';
      card.style.removeProperty('width');
      card.style.removeProperty('height');
    });

    this.#openCard = card;
    card.focus();
  }

  #close(card) {
    if (!this.#openCard) return;
    this.overlay?.classList.remove('active');
    card.classList.remove('open');
    document.body.style.overflow = '';

    card.style.top = `${this.#lastRect.top}px`;
    card.style.left = `${this.#lastRect.left}px`;
    card.style.width = `${this.#lastRect.width}px`;
    card.style.height = `${this.#lastRect.height}px`;

    setTimeout(() => {
      card.removeAttribute('style');
      this.#placeholder?.replaceWith(card);
      this.#placeholder = null;
      if (this.overlay) this.overlay.hidden = true;
      this.#openCard = null;
    }, 300);
  }
}

if (!customElements.get('ix-brands-folder')) {
  customElements.define('ix-brands-folder', IxBrandsFolder);
}
